import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Collapse,
  Paper,
  Stack,
  Chip,
  Pagination,
} from "@mui/material";
import { tokens } from "../../../theme";
import superAdminService from "../../../services/SüperAdminServices/superAdminService";
import DataTable from "../../../components/SüperAdminComponents/DataTable";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export default function FormsPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: "",
    fileName: "",
    type: "",
    dateFrom: "",
    dateTo: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchForms = useCallback(async () => {
    try {
      setLoading(true);
      const response = await superAdminService.getAllForms({
        withMeta: true,
        params: appliedFilters,
        headers: {
          "x-bone-pageindex": pageIndex,
          "x-bone-limit": pageSize,
          "x-bone-sortby": sortBy,
          "x-bone-sortorder": sortOrder,
        },
      });
      setForms(response.data || []);
      setTotalCount(Number(response.headers?.["x-total-count"] || 0));
      setTotalPages(Number(response.headers?.["x-total-pages"] || 1));
    } catch (error) {
      console.error("Error fetching forms:", error);
      setSnackbar({
        open: true,
        message: "Error fetching forms",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, pageIndex, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const handleView = (form) => {
    setSelectedForm(form);
    setViewDialogOpen(true);
  };

  const columns = [
    { id: "name", label: "Name" },
    { id: "description", label: "Description" },
    {
      id: "File",
      label: "File",
      format: (value) => value?.name || "-",
    },
  ];

  const sanitizeFilters = useMemo(() => () => {
    const output = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) output[key] = value;
    });
    return output;
  }, [filters]);

  const applyFilters = () => {
    setPageIndex(0);
    setAppliedFilters(sanitizeFilters());
  };

  const resetFilters = () => {
    setFilters({
      name: "",
      fileName: "",
      type: "",
      dateFrom: "",
      dateTo: "",
    });
    setPageIndex(0);
    setAppliedFilters({});
  };

  const activeFilterCount = Object.keys(appliedFilters || {}).length;

  const getFilenameFromHeaders = (headers) => {
    const contentDisposition = headers?.["content-disposition"] || "";
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    return match?.[1] || `forms-export-${Date.now()}.xlsx`;
  };

  const handleExcelExport = async () => {
    try {
      setExportLoading(true);
      const response = await superAdminService.exportExcel({
        resource: "forms",
        filters: appliedFilters,
        sortBy,
        sortOrder,
      });
      const fileName = getFilenameFromHeaders(response.headers);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "Excel downloaded successfully", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Excel export failed", severity: "error" });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Box sx={{ 
      p: 3, 
      width: "100%", 
      minWidth: "100%", 
      maxWidth: "100%", 
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, width: "100%", minWidth: "100%" }}>
        <DescriptionIcon sx={{ fontSize: 40, color: colors.blueAccent[500] }} />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            background: `linear-gradient(135deg, ${colors.grey[100]} 0%, ${colors.blueAccent[200]} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Forms Management
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<FilterListIcon />}
              endIcon={filtersOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              onClick={() => setFiltersOpen((prev) => !prev)}
              sx={{
                color: colors.grey[100],
                backgroundColor: filtersOpen ? colors.blueAccent[700] : colors.primary[300],
                "&:hover": {
                  backgroundColor: filtersOpen ? colors.blueAccent[800] : colors.primary[200],
                },
              }}
            >
              Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExcelExport}
              disabled={exportLoading}
            >
              {exportLoading ? "Exporting..." : "Export Excel"}
            </Button>
          </Stack>
        </Box>
      </Box>

      <Collapse in={filtersOpen} timeout={250} unmountOnExit>
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`, border: `1px solid ${colors.grey[800]}30` }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1.5, mb: 1.5 }}>
            <TextField
              label="Form Name"
              size="small"
              value={filters.name}
              onChange={(event) => setFilters((prev) => ({ ...prev, name: event.target.value }))}
            />
            <TextField
              label="File Name"
              size="small"
              value={filters.fileName}
              onChange={(event) => setFilters((prev) => ({ ...prev, fileName: event.target.value }))}
            />
            <TextField
              select
              label="Type"
              size="small"
              value={filters.type}
              onChange={(event) => setFilters((prev) => ({ ...prev, type: event.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="for me">For Me</MenuItem>
              <MenuItem value="for patients">For Patients</MenuItem>
            </TextField>
            <TextField
              label="Date From"
              type="date"
              size="small"
              value={filters.dateFrom}
              onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Date To"
              type="date"
              size="small"
              value={filters.dateTo}
              onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={applyFilters}>Apply Filters</Button>
            <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>Clear</Button>
          </Stack>
        </Paper>
      </Collapse>

      <DataTable
        columns={columns}
        data={forms}
        onView={handleView}
        loading={loading}
        emptyMessage="No forms found"
      />
      <Paper elevation={0} sx={{ mt: 2, p: 1.5, borderRadius: 2, background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`, border: `1px solid ${colors.grey[800]}30` }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={`Total ${totalCount}`} />
            <Chip size="small" label={`Page ${pageIndex + 1}/${totalPages}`} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField select label="Rows" size="small" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPageIndex(0); }} sx={{ minWidth: 90 }}>
              {[10, 20, 50, 100].map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
            </TextField>
            <TextField select label="Sort By" size="small" value={sortBy} onChange={(event) => { setSortBy(event.target.value); setPageIndex(0); }} sx={{ minWidth: 120 }}>
              <MenuItem value="createdAt">Created</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="type">Type</MenuItem>
            </TextField>
            <TextField select label="Direction" size="small" value={sortOrder} onChange={(event) => { setSortOrder(event.target.value); setPageIndex(0); }} sx={{ minWidth: 110 }}>
              <MenuItem value="DESC">DESC</MenuItem>
              <MenuItem value="ASC">ASC</MenuItem>
            </TextField>
          </Stack>
          <Pagination count={Math.max(totalPages, 1)} page={Math.min(pageIndex + 1, Math.max(totalPages, 1))} onChange={(_, page) => setPageIndex(page - 1)} shape="rounded" />
        </Box>
      </Paper>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.grey[100] }}>
          Form Details
        </DialogTitle>
        <DialogContent>
          {selectedForm && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                Name
              </Typography>
              <Typography variant="body1" sx={{ color: colors.grey[100], mb: 2 }}>
                {selectedForm.name}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                Description
              </Typography>
              <Typography variant="body1" sx={{ color: colors.grey[100], mb: 2 }}>
                {selectedForm.description || "-"}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>
                File
              </Typography>
              <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                {selectedForm.File?.name || "-"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setViewDialogOpen(false)}
            sx={{ color: colors.grey[100] }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

